import { useEffect, useState } from 'react'
import { CheckCircle2, XCircle, Scale } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface FrameworkTech {
  technique_id: string
  technique_name: string
  controls: string[]
  rule_count: number
  covered: boolean
  actor_count: number
}
interface Framework {
  framework: string
  label: string
  techniques: FrameworkTech[]
  covered_count: number
  uncovered_count: number
  controls: string[]
  control_count: number
  coverage_pct: number
}

export default function CompliancePosturePage() {
  const [data, setData] = useState<{ frameworks: Framework[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [picked, setPicked] = useState<Framework | null>(null)

  useEffect(() => {
    api.get('/api/cyber/compliance-crosswalk')
      .then(r => { setData(r.data); setPicked(r.data?.frameworks?.[0] || null) })
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="card p-6 text-center text-text-muted text-sm">Computing crosswalk…</div>
  if (!data) return <div className="card p-6 text-center text-text-muted text-sm">No crosswalk data.</div>

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Scale size={24} className="text-blue" /> Compliance Posture
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          MITRE ATT&CK → control framework mapping with detection coverage from our Sigma library.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {data.frameworks.map(f => (
          <button key={f.framework} type="button" onClick={() => setPicked(f)}
            className={clsx('card p-4 text-left transition-all',
              picked?.framework === f.framework ? 'ring-2 ring-blue' : 'hover:scale-[1.02]')}>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">{f.label}</div>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-3xl font-bold font-mono text-text-primary">{f.coverage_pct}%</span>
              <span className="text-[10px] text-text-muted">coverage</span>
            </div>
            <div className="mt-2 text-[11px] text-text-secondary">
              {f.covered_count} of {f.covered_count + f.uncovered_count} mapped techniques have detection
            </div>
            <div className="text-[10px] text-text-muted mt-0.5">{f.control_count} controls covered</div>
            {/* Bar */}
            <div className="h-1.5 rounded-full bg-surface-raised mt-2 overflow-hidden">
              <div className={clsx('h-full',
                f.coverage_pct >= 80 ? 'bg-emerald'
                : f.coverage_pct >= 50 ? 'bg-amber-light'
                : 'bg-rose')} style={{ width: `${f.coverage_pct}%` }} />
            </div>
          </button>
        ))}
      </div>

      {picked && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-3">{picked.label} — technique coverage</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                  <th className="text-left py-2 px-2">Technique</th>
                  <th className="text-left py-2 px-2">Controls</th>
                  <th className="text-right py-2 px-2">Sigma rules</th>
                  <th className="text-right py-2 px-2">Actors</th>
                  <th className="text-center py-2 px-2">Detected</th>
                </tr>
              </thead>
              <tbody>
                {picked.techniques.map(t => (
                  <tr key={t.technique_id} className="border-b border-border/50 hover:bg-surface-hover">
                    <td className="py-1.5 px-2">
                      <span className="font-mono font-semibold">{t.technique_id}</span>
                      <span className="ml-2 text-text-secondary">{t.technique_name}</span>
                    </td>
                    <td className="py-1.5 px-2 text-[10px] text-text-muted">
                      {t.controls.join(', ')}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono">{t.rule_count}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{t.actor_count}</td>
                    <td className="py-1.5 px-2 text-center">
                      {t.covered ? <CheckCircle2 size={14} className="text-emerald inline" />
                                  : <XCircle size={14} className="text-rose inline" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
